/**
 * Studies Module - Clinical Studies Management
 * Sub-tab inside DataTab for Super Admin
 */

import { useState, useEffect, useCallback } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  FlaskConical, Plus, Users, Calendar, ClipboardList,
  ChevronRight, CheckCircle, AlertTriangle, Clock,
  BarChart3, Download, X, Search, UserPlus, Eye,
  ArrowLeft, Trash2, Target, Activity, Zap
} from "lucide-react";
import QuickEnrollByQuestionnaire from "@/components/studies/QuickEnrollByQuestionnaire";
import ProAlertsDashboard from "@/components/studies/ProAlertsDashboard";

// ==============================================
// MAIN STUDIES VIEW
// ==============================================
export default function StudiesView() {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [view, setView] = useState("list"); // list | detail | create | alerts
  const [showQuickEnroll, setShowQuickEnroll] = useState(false);

  const fetchStudies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/studies");
      setStudies(res.data);
    } catch (err) {
      // error handled by toast
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStudies(); }, [fetchStudies]);

  if (view === "create") {
    return <CreateStudyForm onBack={() => { setView("list"); fetchStudies(); }} />;
  }

  if (view === "detail" && selectedStudy) {
    return <StudyDetail studyId={selectedStudy} onBack={() => { setView("list"); setSelectedStudy(null); fetchStudies(); }} />;
  }

  if (view === "alerts") {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setView("list")}
          data-testid="back-to-studies-btn"
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux études
        </button>
        <ProAlertsDashboard />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">
            {studies.length} etude{studies.length !== 1 ? "s" : ""} configuree{studies.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView("alerts")}
            data-testid="view-pro-alerts-btn"
            className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors"
          >
            <AlertTriangle className="w-4 h-4" />
            Alertes PRO
          </button>
          <button
            onClick={() => setShowQuickEnroll(true)}
            data-testid="quick-enroll-btn"
            className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors"
          >
            <Zap className="w-4 h-4" />
            Enrôlement rapide
          </button>
          <button
            onClick={() => setView("create")}
            data-testid="create-study-btn"
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvelle etude
          </button>
        </div>
      </div>

      {/* Studies List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Chargement...</div>
      ) : studies.length === 0 ? (
        <div className="text-center py-16">
          <FlaskConical className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">Aucune etude configuree</p>
          <p className="text-sm text-slate-400 mt-1">Creez votre premiere etude clinique</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {studies.map(study => (
            <StudyCard
              key={study.id}
              study={study}
              onClick={() => { setSelectedStudy(study.id); setView("detail"); }}
            />
          ))}
        </div>
      )}

      <QuickEnrollByQuestionnaire
        open={showQuickEnroll}
        onClose={() => setShowQuickEnroll(false)}
        onEnrolled={() => fetchStudies()}
      />
    </div>
  );
}


// ==============================================
// STUDY CARD
// ==============================================
function StudyCard({ study, onClick }) {
  const statusColors = {
    active: "bg-emerald-50 text-emerald-700",
    paused: "bg-amber-50 text-amber-700",
    completed: "bg-blue-50 text-blue-700",
    archived: "bg-slate-100 text-slate-500",
  };

  return (
    <button
      onClick={onClick}
      data-testid={`study-card-${study.id}`}
      className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-indigo-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-slate-800 truncate">{study.name}</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[study.status] || statusColors.active}`}>
              {study.status}
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-3">{study.sponsor} {study.protocolNumber ? `- ${study.protocolNumber}` : ""}</p>
          {study.description && (
            <p className="text-sm text-slate-400 line-clamp-1 mb-3">{study.description}</p>
          )}
          <div className="flex items-center gap-6 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {study.enrolledCount || 0} patient{(study.enrolledCount || 0) !== 1 ? "s" : ""}
            </span>
            <span className="flex items-center gap-1">
              <ClipboardList className="w-3.5 h-3.5" />
              {study.questionnaires?.length || 0} questionnaire{(study.questionnaires?.length || 0) !== 1 ? "s" : ""}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {study.visitSchedule?.length || 0} visite{(study.visitSchedule?.length || 0) !== 1 ? "s" : ""}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {study.startDate}
            </span>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0 mt-2" />
      </div>
    </button>
  );
}


// ==============================================
// CREATE STUDY FORM
// ==============================================
function CreateStudyForm({ onBack }) {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    sponsor: "",
    protocolNumber: "",
    description: "",
    startDate: new Date().toISOString().split("T")[0],
    endDate: "",
    questionnaires: [],
    inclusionCriteria: {
      cancerTypes: [],
      stages: [],
      treatmentTypes: [],
      biomarkers: [],
      ecogMax: null,
      therapeuticLines: [],
    },
    visitSchedule: [
      { name: "Baseline", dayOffset: 0, windowDays: 7, questionnaires: [] },
    ],
  });

  // Reference data for dropdowns
  const [refData, setRefData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [qRes, refRes] = await Promise.all([
          api.get("/studies/questionnaires"),
          api.get("/clinical/reference-data"),
        ]);
        setQuestionnaires(qRes.data);
        setRefData(refRes.data);
      } catch (err) {
        // error handled by toast
      }
    };
    load();
  }, []);

  const updateField = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const updateCriteria = (key, value) => setForm(f => ({
    ...f,
    inclusionCriteria: { ...f.inclusionCriteria, [key]: value },
  }));

  const toggleArrayItem = (key, item) => {
    const current = form.inclusionCriteria[key] || [];
    const updated = current.includes(item)
      ? current.filter(i => i !== item)
      : [...current, item];
    updateCriteria(key, updated);
  };

  const toggleQuestionnaire = (code) => {
    const current = form.questionnaires;
    const updated = current.includes(code)
      ? current.filter(c => c !== code)
      : [...current, code];
    updateField("questionnaires", updated);
  };

  const addVisit = () => {
    const visits = [...form.visitSchedule];
    const lastOffset = visits.length > 0 ? visits[visits.length - 1].dayOffset : 0;
    visits.push({
      name: `Semaine ${Math.round((lastOffset + 42) / 7)}`,
      dayOffset: lastOffset + 42,
      windowDays: 5,
      questionnaires: [],
    });
    updateField("visitSchedule", visits);
  };

  const removeVisit = (idx) => {
    const visits = form.visitSchedule.filter((_, i) => i !== idx);
    updateField("visitSchedule", visits);
  };

  const updateVisit = (idx, key, value) => {
    const visits = [...form.visitSchedule];
    visits[idx] = { ...visits[idx], [key]: value };
    updateField("visitSchedule", visits);
  };

  const toggleVisitQuestionnaire = (visitIdx, code) => {
    const visits = [...form.visitSchedule];
    const current = visits[visitIdx].questionnaires || [];
    visits[visitIdx].questionnaires = current.includes(code)
      ? current.filter(c => c !== code)
      : [...current, code];
    updateField("visitSchedule", visits);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.sponsor) {
      toast.error("Nom et sponsor requis");
      return;
    }
    try {
      setSaving(true);
      await api.post("/studies", form);
      toast.success("Etude creee avec succes");
      onBack();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la creation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="w-4 h-4" /> Retour aux etudes
      </button>

      <h3 className="text-lg font-bold text-slate-800">Creer une nouvelle etude</h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General Info */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h4 className="font-semibold text-slate-700 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-indigo-500" />
            Informations generales
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nom de l'etude *</label>
              <input
                value={form.name}
                onChange={e => updateField("name", e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                placeholder="Ex: HANA-RWE-Poumon-2026"
                data-testid="study-name-input"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Sponsor *</label>
              <input
                value={form.sponsor}
                onChange={e => updateField("sponsor", e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                placeholder="Ex: MSD, Roche, Pfizer..."
                data-testid="study-sponsor-input"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">N de protocole</label>
              <input
                value={form.protocolNumber}
                onChange={e => updateField("protocolNumber", e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                placeholder="Ex: HANA-RWE-2026-001"
              />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs text-slate-500 mb-1">Date debut</label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={e => updateField("startDate", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-slate-500 mb-1">Date fin</label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={e => updateField("endDate", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => updateField("description", e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              rows={2}
              placeholder="Description de l'etude..."
            />
          </div>
        </div>

        {/* Inclusion Criteria */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h4 className="font-semibold text-slate-700 flex items-center gap-2">
            <Target className="w-4 h-4 text-indigo-500" />
            Criteres d'inclusion (matching automatique)
          </h4>

          {/* Cancer Types */}
          <div>
            <label className="block text-xs text-slate-500 mb-2">Types de cancer</label>
            <div className="flex flex-wrap gap-2">
              {(refData?.cancerTypes || []).map(ct => (
                <button
                  key={ct}
                  type="button"
                  onClick={() => toggleArrayItem("cancerTypes", ct)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    form.inclusionCriteria.cancerTypes.includes(ct)
                      ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {ct}
                </button>
              ))}
            </div>
          </div>

          {/* Stages */}
          <div>
            <label className="block text-xs text-slate-500 mb-2">Stades</label>
            <div className="flex flex-wrap gap-2">
              {(refData?.stages || []).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleArrayItem("stages", s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    form.inclusionCriteria.stages.includes(s)
                      ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Treatment Types */}
          <div>
            <label className="block text-xs text-slate-500 mb-2">Types de traitement</label>
            <div className="flex flex-wrap gap-2">
              {(refData?.treatmentTypes || []).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleArrayItem("treatmentTypes", t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    form.inclusionCriteria.treatmentTypes.includes(t)
                      ? "bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* ECOG Max */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">ECOG maximum</label>
            <select
              value={form.inclusionCriteria.ecogMax ?? ""}
              onChange={e => updateCriteria("ecogMax", e.target.value ? parseInt(e.target.value) : null)}
              className="w-48 px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="">Pas de limite</option>
              {[0, 1, 2, 3, 4].map(n => (
                <option key={n} value={n}>{`ECOG <= ${n}`}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Questionnaires */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h4 className="font-semibold text-slate-700 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-indigo-500" />
            Questionnaires PRO
          </h4>
          <div className="grid gap-3">
            {questionnaires.map(q => (
              <label
                key={q.code}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  form.questionnaires.includes(q.code)
                    ? "border-indigo-300 bg-indigo-50"
                    : "border-slate-200 hover:bg-slate-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.questionnaires.includes(q.code)}
                  onChange={() => toggleQuestionnaire(q.code)}
                  className="rounded border-slate-300"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-700">{q.name}</p>
                    {q.cancerTypes?.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-medium">
                        {q.cancerTypes.join(", ")}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400">{q.description} - {q.itemCount} items (~{q.estimatedMinutes} min)</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Visit Schedule */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-indigo-500" />
              Calendrier des visites
            </h4>
            <button
              type="button"
              onClick={addVisit}
              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
            >
              <Plus className="w-3.5 h-3.5" /> Ajouter visite
            </button>
          </div>

          <div className="space-y-3">
            {form.visitSchedule.map((visit, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    value={visit.name}
                    onChange={e => updateVisit(idx, "name", e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                    placeholder="Nom de la visite"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">J+</span>
                    <input
                      type="number"
                      value={visit.dayOffset}
                      onChange={e => updateVisit(idx, "dayOffset", parseInt(e.target.value) || 0)}
                      className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-center"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Fenetre</span>
                    <input
                      type="number"
                      value={visit.windowDays}
                      onChange={e => updateVisit(idx, "windowDays", parseInt(e.target.value) || 3)}
                      className="w-16 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-center"
                    />
                    <span className="text-xs text-slate-500">j</span>
                  </div>
                  {idx > 0 && (
                    <button type="button" onClick={() => removeVisit(idx)} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {/* Questionnaires for this visit */}
                {form.questionnaires.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-slate-500">Questionnaires:</span>
                    {form.questionnaires.map(qc => {
                      const q = questionnaires.find(x => x.code === qc);
                      return (
                        <button
                          key={qc}
                          type="button"
                          onClick={() => toggleVisitQuestionnaire(idx, qc)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            (visit.questionnaires || []).includes(qc)
                              ? "bg-indigo-100 text-indigo-700"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {q?.name || qc}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onBack} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="submit-study-btn"
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Creation..." : "Creer l'etude"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ==============================================
// STUDY DETAIL VIEW
// ==============================================
function StudyDetail({ studyId, onBack }) {
  const [study, setStudy] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [eligiblePatients, setEligiblePatients] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [showEligible, setShowEligible] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [studyRes, dashRes, enrollRes] = await Promise.all([
        api.get(`/studies/${studyId}`),
        api.get(`/studies/${studyId}/dashboard`),
        api.get(`/studies/${studyId}/enrollments`),
      ]);
      setStudy(studyRes.data);
      setDashboard(dashRes.data);
      setEnrollments(enrollRes.data);
    } catch (err) {
      // error handled by toast
      toast.error("Erreur chargement de l'etude");
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const findEligible = async () => {
    try {
      const res = await api.get(`/studies/${studyId}/eligible-patients`);
      setEligiblePatients(res.data);
      setShowEligible(true);
    } catch (err) {
      toast.error("Erreur lors du matching");
    }
  };

  const enrollPatient = async (patientId) => {
    try {
      setEnrolling(true);
      await api.post(`/studies/${studyId}/enroll`, {
        patientId,
        consentDate: new Date().toISOString().split("T")[0],
        consentMethod: "electronic",
      });
      toast.success("Patient enrole avec succes");
      setShowEligible(false);
      setEligiblePatients(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enrolement");
    } finally {
      setEnrolling(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/studies/${studyId}/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `study_export_${study?.protocolNumber || studyId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export telecharge");
    } catch (err) {
      toast.error("Erreur export");
    }
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Chargement...</div>;
  if (!study) return null;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-slate-400 hover:text-slate-700">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h3 className="font-bold text-slate-800">{study.name}</h3>
            <p className="text-sm text-slate-500">{study.sponsor} {study.protocolNumber ? `- ${study.protocolNumber}` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AutoEnrollToggle
            studyId={studyId}
            initial={study.autoEnroll || false}
            onToggle={fetchData}
          />
          <button
            onClick={findEligible}
            data-testid="find-eligible-btn"
            className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors"
          >
            <Target className="w-4 h-4" />
            Matching auto
          </button>
          <button
            onClick={handleExport}
            data-testid="export-study-btn"
            className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors"
          >
            <Download className="w-4 h-4" />
            Exporter
          </button>
        </div>
      </div>

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-4 gap-4">
          <DashCard icon={Users} label="Patients enroles" value={dashboard.enrolledCount} color="text-indigo-600" bg="bg-indigo-50" />
          <DashCard icon={CheckCircle} label="Adherence" value={`${dashboard.adherence?.adherenceRate || 0}%`} color="text-emerald-600" bg="bg-emerald-50" />
          <DashCard icon={Clock} label="Visites en retard" value={dashboard.adherence?.overdue || 0} color={dashboard.adherence?.overdue > 0 ? "text-red-600" : "text-slate-600"} bg={dashboard.adherence?.overdue > 0 ? "bg-red-50" : "bg-slate-50"} />
          <DashCard icon={ClipboardList} label="Reponses collectees" value={dashboard.totalResponses || 0} color="text-blue-600" bg="bg-blue-50" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        {[
          { id: "overview", label: "Vue d'ensemble" },
          { id: "enrollments", label: `Patients (${enrollments.length})` },
          { id: "schedule", label: "Calendrier" },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h4 className="font-semibold text-slate-700 text-sm">Criteres d'inclusion</h4>
            {study.inclusionCriteria?.cancerTypes?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                <span className="text-xs text-slate-500 mr-1">Cancer:</span>
                {study.inclusionCriteria.cancerTypes.map(c => (
                  <span key={c} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs">{c}</span>
                ))}
              </div>
            )}
            {study.inclusionCriteria?.stages?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                <span className="text-xs text-slate-500 mr-1">Stades:</span>
                {study.inclusionCriteria.stages.map(s => (
                  <span key={s} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs">{s}</span>
                ))}
              </div>
            )}
            {study.inclusionCriteria?.treatmentTypes?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                <span className="text-xs text-slate-500 mr-1">Traitement:</span>
                {study.inclusionCriteria.treatmentTypes.map(t => (
                  <span key={t} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs">{t}</span>
                ))}
              </div>
            )}
            {study.inclusionCriteria?.ecogMax != null && (
              <p className="text-xs text-slate-500">ECOG max: {study.inclusionCriteria.ecogMax}</p>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h4 className="font-semibold text-slate-700 text-sm">Questionnaires</h4>
            {study.questionnaires?.map(q => (
              <div key={q} className="flex items-center gap-2">
                <ClipboardList className="w-3.5 h-3.5 text-indigo-500" />
                <span className="text-sm text-slate-600">{q.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "enrollments" && (
        <div className="space-y-3">
          {enrollments.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500 text-sm">Aucun patient enrole</p>
              <button onClick={findEligible} className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                Lancer le matching automatique
              </button>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Patient</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Cancer</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Protocole</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Consentement</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Prochaine visite</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Progression</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {enrollments.map(e => {
                    const totalVisits = e.plannedVisits?.length || 0;
                    const completedVisits = e.completedVisits?.length || 0;
                    const progress = totalVisits > 0 ? Math.round(completedVisits / totalVisits * 100) : 0;
                    return (
                      <tr key={e.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-700">{e.patientName}</td>
                        <td className="px-4 py-3 text-slate-500">{e.cancerType}</td>
                        <td className="px-4 py-3 text-slate-500">{e.protocol || "-"}</td>
                        <td className="px-4 py-3 text-slate-500">{e.consentDate}</td>
                        <td className="px-4 py-3">
                          {e.nextVisit ? (
                            <span className="text-xs text-slate-600">{e.nextVisit.name} - {e.nextVisit.dueDate}</span>
                          ) : (
                            <span className="text-xs text-emerald-600">Termine</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${progress}%` }} />
                            </div>
                            <span className="text-xs text-slate-500">{completedVisits}/{totalVisits}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "schedule" && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="space-y-4">
            {study.visitSchedule?.map((visit, idx) => (
              <div key={idx} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm">
                  J+{visit.dayOffset}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-700 text-sm">{visit.name}</p>
                  <p className="text-xs text-slate-500">Fenetre: +/- {visit.windowDays} jours</p>
                </div>
                <div className="flex gap-2">
                  {visit.questionnaires?.map(q => (
                    <span key={q} className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs">{q.replace(/_/g, " ")}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Eligible Patients Modal */}
      {showEligible && eligiblePatients && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="font-bold text-slate-800">Matching automatique</h3>
                <p className="text-sm text-slate-500">{eligiblePatients.eligibleCount} patient{eligiblePatients.eligibleCount !== 1 ? "s" : ""} eligible{eligiblePatients.eligibleCount !== 1 ? "s" : ""}</p>
              </div>
              <button onClick={() => setShowEligible(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {eligiblePatients.patients.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500 text-sm">Aucun patient eligible trouve</p>
                  <p className="text-xs text-slate-400 mt-1">Verifiez les criteres d'inclusion ou les evaluations cliniques des patients</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {eligiblePatients.patients.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-700 text-sm">{p.firstName} {p.lastName}</p>
                        <p className="text-xs text-slate-500">{p.cancerType} {p.cancerSubtype ? `(${p.cancerSubtype})` : ""} - {p.stage} - {p.treatmentType}</p>
                        {p.biomarkers?.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {p.biomarkers.map(b => (
                              <span key={b} className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px]">{b}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => enrollPatient(p.id)}
                        disabled={enrolling}
                        data-testid={`enroll-patient-${p.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        Enroler
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ==============================================
// HELPER: Dashboard Card
// ==============================================
function DashCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className={`${bg} rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}


// ==============================================
// HELPER: Auto-Enroll Toggle
// ==============================================
function AutoEnrollToggle({ studyId, initial, onToggle }) {
  const [enabled, setEnabled] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setEnabled(initial); }, [initial]);

  const handleToggle = async () => {
    try {
      setSaving(true);
      const res = await api.post(`/studies/${studyId}/toggle-auto-enroll`);
      setEnabled(res.data?.autoEnroll);
      toast.success(
        res.data?.autoEnroll
          ? "Auto-enrôlement activé : nouveaux patients éligibles rejoindront l'étude"
          : "Auto-enrôlement désactivé"
      );
      onToggle?.();
    } catch {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={saving}
      data-testid="auto-enroll-toggle"
      title="Enrôle automatiquement tout nouveau patient correspondant aux critères d'inclusion"
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
        enabled
          ? "bg-indigo-600 text-white hover:bg-indigo-700"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      <Activity className="w-4 h-4" />
      Auto-enrôl {enabled ? "ON" : "OFF"}
    </button>
  );
}
